import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import NavBar from '../components/NavBar';
import MobileMenu from '../components/MobileMenu';
import { useTheme } from '../contexts/ThemeContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { LockoutScreen, TrialBanner } from '../components/SubscriptionGate';
import { useStorageInfo } from '../hooks/useStorageInfo';
import StorageWarningBanner from '../components/StorageWarningBanner';

function MainLayout() {
  const { theme } = useTheme();
  const { isMobile } = useMediaQuery();
  const { isAuthenticated, userId } = useAuth();
  const { subscription } = useSubscription();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Only fetch storage info when authenticated and access is granted
  const { storageInfo } = useStorageInfo(
    isAuthenticated && subscription.accessGranted ? userId : null
  );

  const isPublicPage = location.pathname === '/';

  // Public page or not authenticated - minimal layout
  if (!isAuthenticated || isPublicPage) {
    return (
      <div style={{
        minHeight: '100vh',
        background: theme.colors.bgPrimary,
        transition: 'background 0.3s ease'
      }}>
        <Outlet />
      </div>
    );
  }

  // Still loading subscription status - show spinner
  if (subscription.status === 'loading') {
    return (
      <div style={{
        minHeight: '100vh',
        background: theme.colors.bgPrimary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: `3px solid ${theme.colors.border}`,
            borderTop: `3px solid ${theme.colors.brand}`,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 16px',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>
            Loading your account...
          </p>
        </div>
      </div>
    );
  }

  // No access - show lockout screen
  if (!subscription.accessGranted) {
    return <LockoutScreen />;
  }

  // Authenticated + access granted - show full app
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      background: theme.colors.bgPrimary,
      transition: 'background 0.3s ease'
    }}>
      {/* Trial banner (only shown during trialing) */}
      <TrialBanner />

      {/* Header */}
      <Header onMenuClick={() => setMobileMenuOpen(true)} />

      {/* Global storage warning — only shown at 95%+ */}
      <StorageWarningBanner storageInfo={storageInfo} context="global" />

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Desktop Sidebar */}
        {!isMobile && (
          <aside style={{
            width: '220px',
            background: theme.colors.bgSecondary,
            borderRight: `1px solid ${theme.colors.border}`,
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            overflowY: 'auto',
            transition: 'all 0.3s ease'
          }}>
            <NavBar />
          </aside>
        )}

        {/* Mobile Menu */}
        {isMobile && (
          <MobileMenu
            isOpen={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Main Content */}
        <main style={{
          flex: 1,
          padding: isMobile ? '16px' : '24px',
          overflowY: 'auto',
          background: theme.colors.bgPrimary,
          transition: 'background 0.3s ease'
        }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default MainLayout;
